import { useMemo } from 'react';

import { useCategoryTreeItem } from '../categoryTree';
import { KuxFilteredCategoriesDTO, tvpil } from '../compat/tvpil';
import type { RhinoImageRatio } from '../image';

import { ImageSize, useImageSettings } from './useImageSettings';

type UseGridSettingsProps = {
  defaultImageRatio?: RhinoImageRatio;
  defaultLandscapeImageHeight?: number;
  defaultPortraitImageHeight?: number;
  gridReferenceId: string;
  railReferenceId: string;
  imageSize?: ImageSize;
};

export type GridSettings = {
  branding: KuxFilteredCategoriesDTO['uiDesignElements'];
  imageHeight?: number;
  imageType?: number;
  imageWidth?: number;
  isGrouped: boolean;
  parentalPinProtected: boolean;
  shouldShowEntitlementIndicator: boolean;
  shouldShowRating: boolean;
  providerName: string;
  title: string;
};

export function useGridSettings({
  imageSize = 'default',
  gridReferenceId,
  railReferenceId,
  defaultPortraitImageHeight,
  defaultLandscapeImageHeight,
  defaultImageRatio,
}: UseGridSettingsProps) {
  const railCategory = useCategoryTreeItem(railReferenceId);
  const gridCategory = useCategoryTreeItem(gridReferenceId)!;
  const imageSettings = useImageSettings({
    referenceId: gridReferenceId,
    imageSize,
    defaultPortraitImageHeight,
    defaultLandscapeImageHeight,
    defaultImageRatio,
  })!;

  const settings = useMemo<GridSettings | undefined>(() => {
    const isGroupedGrid = Boolean(tvpil.dms.GridCharacteristics?.GRID_TYPES.includes(gridReferenceId));

    if (!gridReferenceId || !railCategory) {
      return undefined;
    }

    return {
      branding: gridCategory.uiDesignElements,
      imageHeight: imageSettings.imageHeight,
      imageRatio: imageSettings.imageRatio,
      imageType: imageSettings.imageType,
      imageWidth: imageSettings.imageWidth,
      isGrouped: isGroupedGrid,
      parentalPinProtected: gridCategory.parentalPinProtected,
      shouldShowEntitlementIndicator: Boolean(gridCategory.asideProperty?.presentEntitlementIndicator),
      shouldShowRating: Boolean(gridCategory.asideProperty?.presentRating),
      providerName: gridCategory.name,
      title: railCategory?.name,
    };
  }, [gridReferenceId, gridCategory, imageSettings, railCategory]);

  return settings;
}
